import {
  Component, OnInit, ExistingProvider, forwardRef, HostListener,
} from '@angular/core';
import {
  UntypedFormGroup, UntypedFormBuilder, Validators, ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormArray,
} from '@angular/forms';
import { noop, isNil } from 'lodash';
import { IGlossary } from 'typings/section-types';

export const SECTION_GLOSSARY_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => GlossarySectionComponent),
  multi: true,
};
@Component({
    selector: 'app-glossary-section',
    templateUrl: './glossary-section.component.html',
    styleUrls: ['./glossary-section.component.scss'],
    providers: [SECTION_GLOSSARY_CONTROL_VALUE_ACCESSOR],
    standalone: false
})
export class GlossarySectionComponent implements OnInit, ControlValueAccessor {
  public form: UntypedFormGroup;

  /** Value we will call whenever our form is touched */
  private onTouchedCallback: Function = noop;

  /** Function we will call whenever the value changes */
  private onChangeCallback: (val: IGlossary) => void = noop;

  public get termFormArray(): UntypedFormArray {
    return this.form.get('term') as UntypedFormArray;
  }

  @HostListener('blur') onBlur() {
    this.onTouchedCallback();
  }

  constructor(
    public fb: UntypedFormBuilder,
  ) {
    this.form = this.fb.group({
      title: ['', Validators.required],
      subtitle: [],
      term: this.fb.array([this.fb.group({
        name: ['', Validators.required],
        description: ['', Validators.required],
      })]),
    });
  }

  ngOnInit() {
    this.form.valueChanges.subscribe((formValue) => {
      this.onChangeCallback(formValue);
    });
  }

  writeValue(value: IGlossary) {
    // We only patch the form if there's a value present.
    //
    if (value) {
      // Set the title and subtitle.
      //
      this.form.get('title').setValue(value.title || '');
      this.form.get('subtitle').setValue(value.subtitle || '');

      // Get the formArray that contains the glossaryTerm formGroups.
      //
      const termFormArray = this.form.get('term') as UntypedFormArray;

      // Prepare the array
      //
      value.term = value.term ? value.term : [];

      // Loop through all formGroups in the formArray.
      //
      value.term.forEach((materialGroup, index) => {
        // Get the formGroup from the array.
        //
        const group = termFormArray.controls[index];

        // If this data has no formcontrols, add a formgroup with the data as the initial value.
        //
        if (isNil(group)) {
          termFormArray.push(this.fb.group(materialGroup));
        }
      });

      // Actually write the new value.
      //
      termFormArray.setValue(value.term);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
}
